import React from 'react';
import { FaSearch, FaHandshake, FaArrowRight, FaWrench } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SEO from '../Components/SEO';
import { buildAboutPageSchema, buildBreadcrumbSchema, PAGE_SEO, SITE_NAME } from '../seoConfig';
import standImage from '../assets/stand2.webp';

const AboutUs = () => {
    const navigate = useNavigate();

    const pillars = [
        { icon: FaSearch, num: '01', title: 'Seleção Rigorosa', desc: 'Cada automóvel passa por vistoria cuidada antes de entrar no stock.' },
        { icon: FaWrench, num: '02', title: 'Mecânica & Oficina', desc: 'Manutenção, reparações e preparação para inspeções no mesmo local.' },
        { icon: FaHandshake, num: '03', title: 'Acompanhamento', desc: 'Do primeiro contacto ao pós-venda — estamos sempre consigo.' },
    ];

    return (
        <div className="bg-black text-white min-h-screen overflow-x-hidden">
            <SEO
                title={PAGE_SEO.about.title}
                description={PAGE_SEO.about.description}
                path={PAGE_SEO.about.path}
                structuredData={[
                    buildAboutPageSchema(),
                    buildBreadcrumbSchema([
                    { name: 'Início', path: '/' },
                    { name: 'Sobre Nós', path: '/sobre' },
                ])]}
            />

            <section className="relative min-h-[70vh] flex items-end overflow-hidden pt-20">
                <img src={standImage} alt={SITE_NAME} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                <div className="ajp-grain absolute inset-0" />

                <div className="relative z-10 w-full max-w-[1500px] mx-auto px-5 lg:px-12 pb-16 pt-32">
                    <p className="section-tag mb-4">Sobre o Stand</p>
                    <h1 className="font-display text-6xl sm:text-8xl text-white tracking-wide leading-none mb-6">
                        RIGOR &<br /><span className="text-gradient">TRANSPARÊNCIA</span>
                    </h1>
                    <p className="text-white/45 text-base max-w-lg leading-relaxed">
                        Na {SITE_NAME}, trabalhamos para oferecer o melhor em compra e venda de automóveis, com serviços de mecânica e estética no mesmo espaço.
                    </p>
                </div>
            </section>

            <section className="ajp-section-alt py-24 border-t border-white/[0.06]">
                <div className="max-w-[1500px] mx-auto px-5 lg:px-12">
                    <div className="flex items-end gap-6 mb-16">
                        <span className="ajp-num hidden sm:block">01</span>
                        <div>
                            <p className="section-tag mb-3">Os Nossos Pilares</p>
                            <h2 className="font-display text-5xl text-white tracking-wide">O QUE NOS DEFINE</h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]">
                        {pillars.map((p) => (
                            <div key={p.num} className="bg-black p-10 group hover:bg-[#0a0a0a] transition-colors">
                                <span className="font-display text-4xl text-red/30 group-hover:text-red transition-colors">{p.num}</span>
                                <div className="w-10 h-10 border border-red/20 flex items-center justify-center text-red my-6">
                                    <p.icon size={14} />
                                </div>
                                <h3 className="font-display text-2xl text-white tracking-widest mb-3">{p.title}</h3>
                                <p className="text-white/35 text-sm leading-relaxed">{p.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 border-t border-white/[0.06] bg-black">
                <div className="max-w-[1500px] mx-auto px-5 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8">
                            <p className="section-tag">Stand & Oficina</p>
                            <h2 className="font-display text-5xl sm:text-6xl text-white tracking-wide leading-none">
                                TUDO NUM<br /><span className="text-gradient">SÓ LUGAR</span>
                            </h2>
                            <p className="text-white/40 text-sm leading-relaxed">
                                Compra, venda, mecânica ligeira, manutenção, inspeções, higienização, lavagem, polimentos e cerâmica — tudo o que o seu automóvel precisa.
                            </p>
                            <div className="grid grid-cols-3 gap-px bg-white/[0.06]">
                                {[
                                    { n: '100%', l: 'Transparência' },
                                    { n: 'AJP', l: 'Confiança' },
                                    { n: 'PT', l: 'Serviço' },
                                ].map((s) => (
                                    <div key={s.l} className="bg-black p-5 text-center">
                                        <div className="font-display text-2xl text-red">{s.n}</div>
                                        <div className="text-[9px] font-bold uppercase tracking-[0.2em] text-white/30 mt-1">{s.l}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="relative aspect-[4/5] overflow-hidden border border-white/[0.06]">
                            <img src={standImage} alt={SITE_NAME} className="w-full h-full object-cover" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        </div>
                    </div>
                </div>
            </section>

            <section className="border-t border-red/20 bg-red py-16">
                <div className="max-w-[1500px] mx-auto px-5 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-8">
                    <h2 className="font-display text-4xl text-black tracking-wide">O TEU PRÓXIMO AUTOMÓVEL</h2>
                    <div className="flex gap-4">
                        <button onClick={() => { navigate('/automoveis'); window.scrollTo(0, 0); }} className="inline-flex items-center gap-2 px-8 py-4 bg-black text-white text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-black transition-colors">
                            Ver Stock <FaArrowRight size={10} />
                        </button>
                        <button onClick={() => { navigate('/contactos'); window.scrollTo(0, 0); }} className="inline-flex items-center gap-2 px-8 py-4 border-2 border-black text-black text-[11px] font-bold uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors">
                            Contactar
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AboutUs;
